import { isNil } from 'lodash';
import moment, { Moment, unitOfTime } from 'moment-timezone';

export const TIMEZONE: string = 'America/Montreal';

interface Options {
  ignoreTime: boolean;
}

export enum WeekDays {
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
  SUNDAY = 7,
}

/**
 * refer yourself to https://devhints.io/moment for formatting information
 */

/**
 * Encapsulate date manipulation to make sure timezone persists in time.
 * This layer should be the ONLY one (except date picker input)
 * to interact with moment library.
 */
export class DateTime {
  private date: Moment;
  private isValid: boolean = true;

  constructor(date?: string) {
    // If no date provided, create today date
    if (!date) this.date = moment.tz(moment(), TIMEZONE);
    else {
      // If date has been provided, check if format is valid
      if (!this.stringDateHasValidFormat(date)) {
        this.isValid = false;
        this.date = moment.tz(moment(), TIMEZONE);
      } else {
        // Ignore the timezone (if exists) and set timezone to montreal
        this.date = moment.tz(date, 'YYYY-MM-DDTHH:mm:ss', TIMEZONE);
      }
    }
  }

  /** Creates a date from the given string or undefined if the string isn't a valid date. */
  public static from(date: string) {
    if (isNil(date)) return;

    const dateTime = new DateTime(date);
    if (!dateTime.isDateValid()) return;

    return dateTime;
  }

  /**
   * Returns the moment object. This should ONLY be use for
   * material ui date picker because it is moment library
   * based.
   */
  public get Moment(): Moment {
    return this.date;
  }

  // Add business days to current date
  public addBusinessDays(daysNumber: number): DateTime {
    let currentDate = this.date.clone();

    // Increment until number of business days have been added
    for (let i = 0; i < daysNumber; i++) {
      // By default, we decrement one by one day
      let daysIncrement = 1;
      // If current date is sunday, jump to monday
      if (currentDate.isoWeekday() === WeekDays.FRIDAY) {
        daysIncrement = 3;
      }
      // If current date is saturday, jump to monday
      if (currentDate.isoWeekday() === WeekDays.SATURDAY) {
        daysIncrement = 2;
      }
      // Decrement
      currentDate = currentDate.add(daysIncrement, 'day');
    }

    return new DateTime(currentDate.format()).clearTime();
  }

  // Add days from today and create new date from the result
  public addDays(daysFromToday: number): DateTime {
    const updatedDate: Moment = this.date.add(daysFromToday, 'days');
    return new DateTime(updatedDate.format());
  }

  public addHours(hours: number): DateTime {
    const updatedDate: Moment = this.date.add(hours, 'hours');
    return new DateTime(updatedDate.format());
  }

  // Add days from today and create new date from the result
  public addMinutes(minutes: number): DateTime {
    const updatedDate: Moment = this.date.add(minutes, 'minutes');
    return new DateTime(updatedDate.format());
  }

  // Add months from today and creat new date from result
  public addMonths(months: number): DateTime {
    const updatedDate: Moment = this.date.add(months, 'month');
    return new DateTime(updatedDate.format());
  }

  // Add seconds from today and create new date from the result
  public addSeconds(minutes: number): DateTime {
    const updatedDate: Moment = this.date.add(minutes, 'seconds');
    return new DateTime(updatedDate.format());
  }

  // Add years from today and create new date from the result
  public addYears(yearsFromToday: number): DateTime {
    const updatedDate: Moment = this.date.add(yearsFromToday, 'years');
    return new DateTime(updatedDate.format());
  }

  // Remove time from date.
  // Ex: 2020-01-01T05:01:01+04:00 is convert to 2020-01-01T00:00:00+04:00
  public clearTime(): DateTime {
    return new DateTime(this.formatToShortDate());
  }

  /** Clones the datetime. */
  public clone(): DateTime {
    return new DateTime(this.Moment.clone().toISOString());
  }

  // Calculate business days between the date and today
  public diffBusinessDaysFromToday(): number {
    let businessDays: number = 0;
    // Get total diff days
    const diffDays = this.diffDays(new DateTime().clearTime());

    let modifier: number = 1;
    // If date is before today, then we must go back in the time
    if (diffDays < 0) modifier = -1;

    // Calculate number of business days
    for (let i = 1; i <= Math.abs(diffDays); i++) {
      // Get next week day
      const weekDay = new DateTime().addDays(i * modifier).getWeekDay();
      // If next week day is saturday or sunday, do not calculate as a business day
      if (weekDay !== WeekDays.SATURDAY && weekDay !== WeekDays.SUNDAY) {
        businessDays++;
      }
    }

    // Return total business days
    return businessDays * modifier;
  }

  // Calculate days between the date and the reference date
  public diffDays(reference: DateTime): number {
    return this.date.diff(reference.Moment, 'days', false);
  }

  // Calculate days number between today and the current date
  public diffDaysFromToday(): number {
    const today = new DateTime().clearTime();
    return this.date.diff(today.Moment, 'days', false);
  }

  public diffHoursFromDate(reference: DateTime): number {
    return this.date.diff(reference.Moment, 'hours', false);
  }

  // Calculate months number between reference date and the current date
  public diffMonthsFromDate(reference: DateTime): number {
    return this.date.diff(reference.Moment, 'months', false);
  }

  public diffSecondsFromDate(reference: DateTime): number {
    return this.date.diff(reference.Moment, 'seconds', false);
  }

  // Returns the difference in years the date of the DateTime instance and a reference date
  public diffYearsFromDate(earlierDate: DateTime, precision: 'precise' | 'rounded' = 'rounded'): number {
    return this.date.diff(earlierDate.Moment, 'years', precision === 'precise');
  }

  // Calculate years number between today and the current date
  public diffYearsFromToday(precision: 'precise' | 'rounded' = 'rounded'): number {
    const today = new DateTime();
    return today.Moment.diff(this.date, 'years', precision === 'precise');
  }

  // Format date to display in Allset web admin portal
  public formatReadableFullDate = (includesWeekDay: boolean): string => {
    // If long version is set, return long format eg. Tuesday, August 6, 2019 5:38 PM
    if (includesWeekDay) return this.date.format('LLLL');

    // Short verion, for same day is only the time eg. 5:38 PM
    if (this.isSameDate(new DateTime(), 'day')) return `${this.date.format('LT')}`;

    // Short verion, for past date eg. Aug 6th 19
    return this.date.format('DD MMM, HH:mm');
  };

  // Format date to display in custom format
  // Get all format options at https://momentjs.com/docs/#/displaying/
  public formatSpecificFormat(format: string, options?: { locale?: string }): string {
    const moment = isNil(options?.locale) ? this.Moment : this.Moment.clone().locale(options.locale);
    return moment.format(format);
  }

  /**
   * Format date to yyyy-mm-ddThh:mm:ss.nnn-05:00.
   * We are setting -05:00 since fortus credit check systems
   * only work with daylight timezone.
   */
  public formatToCreditCheck = (): string => this.date.format('YYYY-MM-DD[T]HH:mm:ss.SSS-05:00');

  // Format date to yyyymmdd which is specific to Fortus
  public formatToFortus = (): string => this.date.format('YYYYMMDD');

  /**
   * Format date to ISO8601 yyyy-mm-ddThh:mm:ss.nnn+|-hh:mm (Format = E8601DZw.d)
   * @param options (optional) ignoreTime - set the time to 00:00:00
   */
  public formatToISO8601 = (options?: Options): string => {
    let date: Moment = this.date;
    // If option ignore time is on, then ignore the time while formatting
    // the time will be set to 00:00:00
    if (options && options.ignoreTime) date = this.date.startOf('day');

    return date.format('YYYY-MM-DD[T]HH:mm:ssZ');
  };

  // Format date to yyyy-mm-dd which is specific to Allset
  public formatToMonthAndYear = (): string => this.date.format('MM YYYY');

  // For web-admin to improve date readability
  public formatToReadableShortDate = (): string => this.date.format('DD-MMM-YYYY');

  // Format date to yyyy-mm-dd which is specific to Allset
  public formatToShortDate = (): string => this.date.format('YYYY-MM-DD');

  // Simply outputs the number of milliseconds since the Unix Epoch
  public formatUnixTimestamp() {
    return this.date.valueOf();
  }

  // Format date to human language based on current language. E.g. 2015-01-01 date returns january 1st 2021
  public formatWebReadable = (languageId: string): string => {
    const translatedDate = this.date.clone().locale(languageId);
    return translatedDate.format('LL');
  };

  // Returns the current day of the month (number from 1 to 31)
  public getDay = () => this.date.date();

  // Returns the last day of the month depending on year.
  public getLastDayOfMonth(year: number, month: number): DateTime {
    // Month 0 = January for consistency
    const referenceDate: DateTime = new DateTime().setDate(year, month);

    /* We are going to add 31 days always to each date, then subtract days until the month is the same as the month
      we are querying
      */
    let relativeDate: DateTime = referenceDate.addDays(31);
    while (relativeDate.getMonth() !== month) {
      relativeDate = relativeDate.addDays(-1);
    }
    return relativeDate;
  }

  // Returns the current month (number from 0 to 11)
  public getMonth = () => this.date.month();

  // Returns next week day date
  public getNextWeekDay = (weekDay: WeekDays): DateTime => {
    if (this.date.isoWeekday() === weekDay) return this;

    let date: Moment;
    // By default, moment selects the week day of the *current* week
    if (this.date.isoWeekday() < weekDay) {
      // If needed week day is smaller (e.g. today is monday (1) and needed week day is
      // wednesday (3)), simply set isoWeekDay to 3
      date = this.date.isoWeekday(weekDay);
    } else {
      // If needed week day is bigger (e.g. today is friday (5) and needed week day is
      // monday (1)) we need go to next week
      date = this.date.isoWeekday(weekDay).add(1, 'week');
    }

    // Ignore time
    return new DateTime(date.format('YYYY-MM-DD'));
  };

  // Returns a date that is the same date and month, but on the first year that is either this year or next year.
  public getSameDateInFutureYearOrToday(): DateTime {
    let date = this.clone();
    while (date.isLessThanTodays()) {
      date = date.addYears(1);
    }

    return date;
  }

  // Returns month as a string
  public getStringMonth = () => this.date.format('MMMM');

  // Returns the number of seconds since the Unix Epoch.
  // Basically, Unix time is a system for describing a point in time.
  public getUnixTime = () => this.date.unix();

  // Get current week day
  public getWeekDay = (): WeekDays => this.date.isoWeekday();

  // Returns the current year of the current date
  public getYear = () => this.date.year();

  /** Returns true if the date is strictly after the given date. */
  public isDateGreaterThan = (startDate: DateTime): boolean => this.date.isAfter(startDate.Moment);

  // Validate if current date is greater or equal to a start date
  public isDateGreaterThanOrEqual = (startDate: DateTime): boolean => this.date.isSameOrAfter(startDate.Moment);

  /** Returns true if the date is strictly before the given date. */
  public isDateInferior = (endDate: DateTime): boolean => this.date.isBefore(endDate.Moment);

  // Validate if current date is inferior or equal to an end date
  public isDateInferiorToOrEqual = (endDate: DateTime): boolean => this.date.isSameOrBefore(endDate.Moment);

  // Validate current date
  public isDateValid = (): boolean => this.isValid;

  // Validate current date is between the specified dates based on the day only
  public isDayBetween = (startDate: DateTime, endDate: DateTime): boolean => {
    return this.date.isSameOrAfter(startDate.Moment, 'day') && this.date.isSameOrBefore(endDate.Moment, 'day');
  };

  // Validate if current date is greater or equal to today
  // ignoring the time
  public isDayGreaterThanTodaysOrEquals = (): boolean => this.date.isSameOrAfter(new DateTime().Moment, 'day');

  // Validate if current date is greater or equal to an end date
  // ignoring the time
  public isDayGreaterToOrEqual = (endDate: DateTime): boolean => this.date.isSameOrAfter(endDate.Moment, 'day');

  // Validate if current date is inferior to an end date
  // ignoring the time
  public isDayInferior = (endDate: DateTime): boolean => this.date.isBefore(endDate.Moment, 'day');

  // Validate if current date is inferior or equal to today
  // ignoring the time
  public isDayInferiorThanTodaysOrEquals = (): boolean => this.date.isSameOrBefore(new DateTime().Moment, 'day');

  // Validate if current date is inferior or equal to an end date
  // ignoring the time
  public isDayInferiorToOrEqual = (endDate: DateTime): boolean => this.date.isSameOrBefore(endDate.Moment, 'day');

  // Validate if current date is less that today
  // ignoring time
  public isLessThanTodays = (): boolean => this.date.isBefore(new DateTime().Moment, 'day');

  /**
   * Validate if current date is the same as startDate
   * If you want to limit the granularity to a unit other than milliseconds, pass it as the second parameter.
   * When including a second parameter, it will match all units equal or larger.
   * Passing in month will check month and year.Passing in day will check day, month, and year.
   */
  public isSameDate = (startDate: DateTime, granularity: unitOfTime.StartOf): boolean =>
    this.date.isSame(startDate.Moment, granularity);

  public isSameDay = (): boolean => {
    return this.isSameDate(new DateTime(), 'day');
  };

  public isSameMonth = (other: DateTime): boolean => {
    return this.Moment.isSame(other.Moment, 'M');
  };

  // Returns true if today is a week end
  public isWeekEnd = (): boolean => {
    // Returns true if we are saturday or sunday
    if (this.date.isoWeekday() === WeekDays.SATURDAY || this.date.isoWeekday() === WeekDays.SUNDAY) return true;
    // Return true if we are friday after 5pm
    if (this.date.isoWeekday() === WeekDays.FRIDAY) {
      return this.date.hour() >= 17;
    }
  };

  // Substract business days to current date
  public removeBusinessDays(daysNumber: number): DateTime {
    let currentDate = this.date.clone();

    // Remove days until we removed the days number
    for (let i = 0; i < daysNumber; i++) {
      // By default, decrement one by one day
      let daysDecrement = 1;
      // If current date is monday jump to friday
      if (currentDate.isoWeekday() === WeekDays.MONDAY) {
        daysDecrement = 3;
      }
      // If current date is sunday, jump to friday
      if (currentDate.isoWeekday() === WeekDays.SUNDAY) {
        daysDecrement = 2;
      }
      // Decrement
      currentDate = currentDate.add(daysDecrement * -1, 'day');
    }

    return new DateTime(currentDate.format()).clearTime();
  }

  // Substract days to current date
  public removeDaysToDate(daysFromToday: number): DateTime {
    const updatedDate: Moment = this.date.add(daysFromToday * -1, 'days');
    return new DateTime(updatedDate.format());
  }

  // Create date from yearm month and date. If only the year is given,
  // the created date will be YYYY-01-01
  public setDate(year: number, month: number = 0, date: number = 1): DateTime {
    this.date = this.date.set('year', year);
    this.date = this.date.set('month', month);
    this.date = this.date.set('date', date);

    return this;
  }

  public setDay(day: number): DateTime {
    this.date = this.date.set('date', day);
    return this;
  }

  public setTime(hours: number, minutes: number, seconds: number): DateTime {
    this.date = this.date.set('hours', hours);
    this.date = this.date.set('minutes', minutes);
    this.date = this.date.set('seconds', seconds);

    return this;
  }

  /**
   * Returns a boolean indicating whether this date belongs to a leap year.
   *
   * Leap years occur each year that is a multiple of 4, except for years evenly divisible by 100 but not by 400.
   */
  public isLeapYear() {
    const year = this.date.year();
    return year % 4 === 0 && !(year % 100 === 0 && !(year % 400 === 0));
  }

  public isLeapDay(): boolean {
    return this.isLeapYear() && this.getMonth() === 1 && this.getDay() === 29;
  }

  /**
   * Check if the string date is in a valid format. This is temporary, until
   * all dates (in DB and Application) use the moment format
   * @param stringDate date in string format
   */
  private stringDateHasValidFormat(stringDate: string): boolean {
    // Date formats we support
    const validFormats: string[] = ['YYYY-MM-DD[T]HH:mm:ssZ', 'YYYY-MM-DD[T]HH:mm:ss.SSSSSSZ', 'YYYY-MM-DD'];

    let date: Moment;
    for (const format of validFormats) {
      date = moment(stringDate, format, true);
      // If the date format matchs, then return true
      if (date.isValid()) return true;
    }

    // If none of the supported format matchs, date
    // is not valid
    return false;
  }
}

export default DateTime;
